import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TripDetailsDialog from "@/components/TripDetailsDialog";
import { Search, CalendarDays, X, Download } from "lucide-react";

const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const label = (d) => d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });

const last7 = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 6);
  return { from, to };
};

const field = "h-11 bg-slate-100 border border-slate-300 focus-visible:border-brand-green focus-visible:ring-brand-green/30 font-mono text-sm placeholder:text-slate-400";

export default function SearchBookingsDialog({ accounts = [], deliveries = [], drivers = [], onUpdated }) {
  const [open, setOpen] = useState(false);
  const [tripDetail, setTripDetail] = useState(null);
  const [accountId, setAccountId] = useState("any");
  const [po, setPo] = useState("");
  const [tracking, setTracking] = useState("");
  const [biz, setBiz] = useState("");
  const [range, setRange] = useState(last7());
  const [rangeTouched, setRangeTouched] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  const hasQuery = accountId !== "any" || po.trim() || tracking.trim() || biz.trim();
  // Apply the date window only when the user explicitly picks a range, or when there's no text query (default recent view).
  const useDate = rangeTouched || !hasQuery;
  const fromStr = range?.from ? fmt(range.from) : null;
  const toStr = range?.to ? fmt(range.to) : fromStr;

  const results = useMemo(() => {
    const p = po.trim().toLowerCase();
    const t = tracking.trim().toLowerCase();
    const bizTokens = biz.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return deliveries
      .filter((d) => {
        if (useDate) {
          const bd = d.booking_date;
          if (!bd || bd < fromStr || bd > toStr) return false;
        }
        if (accountId !== "any" && d.account_id !== accountId) return false;
        if (p && !(d.po_number || "").toLowerCase().includes(p)) return false;
        if (t && !`${d.delivery_number || ""} ${d.tracking_token || ""}`.toLowerCase().includes(t)) return false;
        if (bizTokens.length) {
          const hay = [d.pickup?.address, d.dropoff?.address, d.pickup?.contact_name, d.dropoff?.contact_name, d.account_name, d.walk_in_customer?.name]
            .filter(Boolean).join(" ").toLowerCase();
          if (!bizTokens.every((tok) => hay.includes(tok))) return false;
        }
        return true;
      })
      .sort((a, c) => (c.booking_date || "").localeCompare(a.booking_date || ""));
  }, [deliveries, accountId, po, tracking, biz, useDate, fromStr, toStr]);

  const reset = () => { setAccountId("any"); setPo(""); setTracking(""); setBiz(""); setRange(last7()); setRangeTouched(false); };

  const exportCsv = () => {
    const cols = [
      ["Tracking #", (d) => d.delivery_number],
      ["Status", (d) => d.status],
      ["Priority", (d) => d.service_priority],
      ["Account Code", (d) => d.account_code],
      ["Account Name", (d) => d.account_name],
      ["Customer", (d) => d.walk_in_customer?.name || ""],
      ["PO #", (d) => d.po_number || ""],
      ["Booking Date", (d) => d.booking_date || ""],
      ["Delivery Date", (d) => d.delivery_date || ""],
      ["Pickup Address", (d) => d.pickup?.address || ""],
      ["Delivery Address", (d) => d.dropoff?.address || ""],
      ["Weight (kg)", (d) => d.weight_kg ?? ""],
      ["Pieces", (d) => d.pieces ?? ""],
      ["Driver #", (d) => d.driver_number || ""],
      ["Driver", (d) => d.driver_name || ""],
      ["Base Rate", (d) => d.pricing?.base_rate ?? ""],
      ["Weight Surcharge", (d) => d.pricing?.weight_surcharge ?? ""],
      ["Fuel Surcharge", (d) => d.pricing?.fuel_surcharge ?? ""],
      ["Subtotal", (d) => d.pricing?.subtotal ?? ""],
      ["GST", (d) => d.pricing?.gst ?? ""],
      ["Total", (d) => d.pricing?.total ?? ""],
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.map((c) => esc(c[0])).join(",")];
    results.forEach((d) => rows.push(cols.map((c) => esc(c[1](d))).join(",")));
    const blob = new Blob(["\uFEFF" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `country-courier-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-12 px-4 font-mono uppercase tracking-wider text-xs border-slate-300" data-testid="search-bookings-btn">
          <Search className="w-4 h-4 mr-2" />Search
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-card border-2" data-testid="search-bookings-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">Search Bookings</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">Account / Customer</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className={field} data-testid="search-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any account</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_code} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">Date Range (optional)</Label>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`${field} justify-start font-normal`} data-testid="search-daterange-btn">
                    <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground" />
                    {rangeTouched ? (range?.from ? (range.to ? `${label(range.from)} – ${label(range.to)}` : label(range.from)) : "Pick a range") : "Any date (last 7 days default)"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-2" align="start">
                  <Calendar mode="range" selected={range} onSelect={(r) => { setRange(r || last7()); setRangeTouched(true); }} numberOfMonths={2} defaultMonth={range?.from} />
                  <div className="flex justify-between p-2 border-t border-border">
                    <Button size="sm" variant="ghost" className="font-mono text-[10px] uppercase" onClick={() => { setRange(last7()); setRangeTouched(false); }} data-testid="search-daterange-any">Any date</Button>
                    <Button size="sm" variant="ghost" className="font-mono text-[10px] uppercase" onClick={() => { setRange(last7()); setRangeTouched(true); }} data-testid="search-daterange-last7">Last 7 days</Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">PO #</Label>
              <Input value={po} onChange={(e) => setPo(e.target.value)} placeholder="Purchase order" className={field} data-testid="search-po-input" />
            </div>
            <div className="space-y-1">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">Tracking #</Label>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="CC-###### or token" className={field} data-testid="search-tracking-input" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">Business Name or Address</Label>
            <Input value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="Street, business or building # (pickup & delivery)" className={field} data-testid="search-biz-input" />
          </div>

          <div className="border-t-2 border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="search-results-count">
                {results.length} result{results.length === 1 ? "" : "s"} · {useDate ? (fromStr === toStr ? label(range.from) : `${label(range.from)} – ${label(range.to)}`) : "all dates"}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={exportCsv} disabled={results.length === 0} className="font-mono text-[10px] uppercase h-7" data-testid="search-export-btn"><Download className="w-3 h-3 mr-1" />Export CSV</Button>
                <Button size="sm" variant="ghost" onClick={reset} className="font-mono text-[10px] uppercase h-7" data-testid="search-reset-btn"><X className="w-3 h-3 mr-1" />Reset</Button>
              </div>
            </div>
            <div className="max-h-[42vh] overflow-y-auto divide-y divide-border border border-border" data-testid="search-results-list">
              {results.map((d) => (
                <div key={d.id} onClick={() => setTripDetail(d)} className="p-3 hover:bg-muted/40 cursor-pointer" data-testid={`search-result-${d.delivery_number}`}>
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <span className="font-mono text-xs text-brand-green font-bold tracking-widest">{d.delivery_number}</span>
                      <span className="font-mono text-[10px] text-muted-foreground ml-2">{d.account_code} · {d.service_priority} · {d.booking_date}</span>
                    </div>
                    <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-slate-300 text-muted-foreground">{d.status}</span>
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground space-y-0.5">
                    <div>↑ {d.pickup?.address}</div>
                    <div>↓ {d.dropoff?.address}</div>
                    <div className="text-brand-green">${d.pricing?.total?.toFixed(2)}{d.po_number ? ` · PO ${d.po_number}` : ""}{d.driver_name ? ` · ${d.driver_number || ""} ${d.driver_name}` : ""}</div>
                  </div>
                </div>
              ))}
              {results.length === 0 && (
                <div className="p-8 text-center font-mono text-xs uppercase text-muted-foreground" data-testid="search-results-empty">No bookings match</div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <TripDetailsDialog trip={tripDetail} drivers={drivers} onClose={() => setTripDetail(null)} onUpdated={onUpdated} />
    </>
  );
}
